<template>
  <div>
    <v-data-table
      :loading="loading"
      :headers="headers"
      :items="items"
      :pagination.sync="sortOptions"
      must-sort
      hide-actions
    >
      <template v-slot:no-data>
        <div class="text-xs-center">{{ noDataText }}</div>
      </template>
      <template v-slot:items="props">
        <tr v-bind:style="targetStyle(props.item.id)">
          <td class="px-2 text-xs-right">
            {{ props.item.id }}
          </td>
          <td class="px-2">
            <IntervalReportName :name="props.item.name" />
          </td>
          <td class="px-2">
            <IntervalReportDimension :value="props.item.query.dimension" />
          </td>
          <td class="px-2 period">
            <IntervalReportPeriod :period="props.item.query.period" />
          </td>
          <td class="px-2">
            <IntervalReportInterval :value="props.item.query.interval" />
          </td>
          <td class="px-2">
            <AppStatus
              @click="updateStatus(props.item, $event)"
              :value="props.item.status"
              color="info"
              with-icon
            />
          </td>
          <td class="px-2">
            <IntervalReportSendInterval
              :value="props.item.notification.sendInterval"
              :status="props.item.status"
            />
          </td>
          <td class="px-2">
            <IntervalReportListClosingDate
              :value="props.item.notification.closingDate"
              :status="props.item.status"
            />
          </td>
          <td class="px-2 text-no-wrap">
            <ButtonDownload :intervalReport="props.item" />
            <ButtonEdit
              @onClickEdit="onClickEdit"
              :id="props.item.id"
              :name="props.item.name"
            />
            <ButtonDelete
              @onClickDelete="onClickDelete"
              :id="props.item.id"
              :name="props.item.name"
            />
          </td>
        </tr>
      </template>
    </v-data-table>
    <IntervalReportModalEdit
      v-if="displayEditModal"
      :intervalReportId="editTargetIntervalReportId"
      :visible="displayEditModal"
      @clickSubmit="onSubmit"
      @cancel="cancel"
    />
  </div>
</template>

<script>
import IntervalReportName from '@/components/Advertiser/Report/IntervalReport/IntervalReportListName'
import IntervalReportDimension from '@/components/Advertiser/Report/IntervalReport/IntervalReportListDimension'
import IntervalReportInterval from '@/components/Advertiser/Report/IntervalReport/IntervalReportListInterval'
import IntervalReportPeriod from '@/components/Advertiser/Report/IntervalReport/IntervalReportListPeriod'
import IntervalReportListClosingDate from '@/components/Advertiser/Report/IntervalReport/IntervalReportListClosingDate'
import IntervalReportSendInterval from '@/components/Advertiser/Report/IntervalReport/IntervalReportListSendInterval'
import ButtonEdit from '@/components/Advertiser/Report/IntervalReport/IntervalReportListButtonEdit'
import ButtonDownload from '@/components/Advertiser/Report/IntervalReport/IntervalReportListButtonCsvDownload'
import ButtonDelete from '@/components/Advertiser/Report/IntervalReport/IntervalReportListButtonDelete'
import IntervalReportModalEdit from '@/components/Advertiser/Report/IntervalReport/IntervalReportModalAdd'
import { mapActions, mapGetters } from 'vuex'
import { snackbarType } from '@/vuex/modules/ui/snackbar/constants'
import { IntervalReportRepository } from '@/models/Report/IntervalReport'

export default {
  name: 'IntervalReportList',
  components: {
    IntervalReportName,
    IntervalReportDimension,
    IntervalReportInterval,
    IntervalReportPeriod,
    IntervalReportListClosingDate,
    IntervalReportSendInterval,
    ButtonEdit,
    ButtonDownload,
    ButtonDelete,
    IntervalReportModalEdit,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      headers: [
        { text: 'id', value: 'id', width: '60', class: 'px-2' },
        { text: 'レポート名', value: 'name', class: 'px-2' },
        { text: 'レポート種別', value: 'query.dimension', class: 'px-2' },
        {
          text: '集計期間',
          value: 'query.period',
          width: '140',
          class: 'px-2',
        },
        { text: '集計間隔', value: 'query.interval', class: 'px-2' },
        { text: '定期メール配信', value: 'status', width: '60', class: 'px-2' },
        {
          text: '送信頻度',
          value: 'notification.sendInterval',
          width: '100',
          class: 'px-2',
        },
        {
          text: 'メール有効期限',
          value: 'notification.closingDate',
          class: 'px-2',
        },
        { text: '操作', sortable: false, width: '80', class: 'px-2' },
      ],
      sortOptions: {
        sortBy: 'id',
        descending: true,
        rowsPerPage: -1,
      },
      displayEditModal: false,
      editTargetIntervalReportId: null,
    }
  },
  computed: {
    ...mapGetters('advertiser/reportIntervalReport', [
      'keyword',
      'advertiserId',
      'pageNo',
      'loading',
    ]),
    noDataText() {
      return this.loading ? '' : 'データがありません。'
    },
  },
  methods: {
    ...mapActions('advertiser/reportIntervalReport', {
      initSearchItems: 'INIT_SEARCH_ITEMS',
      setIntervalReport: 'SET_INTERVAL_REPORT',
    }),
    ...mapActions('snackbar', {
      showSnackbar: 'SHOW_SNACKBAR',
    }),
    fetchIntervalReport() {
      this.setIntervalReport({
        keyword: this.keyword,
        advertiserId: this.advertiserId,
        pageNo: this.pageNo,
      })
    },
    initSearch() {
      this.initSearchItems(this.$route.params.advertiserId)
      this.fetchIntervalReport()
    },
    async updateStatus(item, isActive) {
      if (item.notification.emails[0] === '') {
        const options = { okText: '編集画面へ', cancelText: 'キャンセル' }
        const message = '送信先アドレスを設定してください。'
        if (await this.$confirm(message, options)) {
          this.onClickEdit(item.id)
        }
        return
      }
      try {
        item.status = await IntervalReportRepository.updateStatus(
          item.id,
          isActive,
        )
        this.showSnackbar({
          type: snackbarType.SUCCESS,
          message: 'ステータスを変更しました。',
        })
      } catch {
        throw new Error('ステータスの変更に失敗しました。')
      }
    },
    onClickEdit(id) {
      this.editTargetIntervalReportId = id
      this.displayEditModal = true
    },
    onSubmit() {
      this.displayEditModal = false
      this.showSnackbar({
        type: snackbarType.SUCCESS,
        message: 'レポート定期メール配信設定を更新しました。',
      })
      this.initSearch()
    },
    cancel() {
      this.displayEditModal = false
    },
    onClickDelete() {
      this.showSnackbar({
        type: snackbarType.SUCCESS,
        message: '削除しました。',
      })
      this.initSearch()
    },
    /**
     * URLパラメーターのidが同一の場合
     * 背景強調のスタイルを返却する
     * @param id 案件ID
     */
    targetStyle(id) {
      if (parseInt(this.$route.query.id) === id) {
        return 'background-color: #ccffff;'
      } else {
        return ''
      }
    },
  },
}
</script>

<style scoped></style>
